import {
  TeamAndCompanyFragment,
  PageStatus,
  Media,
} from '../../../types/__generated__';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import React from 'react';
import styles from './TeamAndCompany.scss';
import { useTranslation } from 'react-i18next';
import { onlyImages } from '../../utils/typeGuards';
import { DEFAULT_TEAM_IMAGE, TRAINER_IMAGE } from '../../constants/images';
import fragment from './TeamAndCompanyFragment.gql';

const FRAGMENT = buildQuery(fragment);

const TeamAndCompany: React.FunctionComponent = () => {
  const { t } = useTranslation('common', { useSuspense: false });
  const result = usePageQuery<TeamAndCompanyFragment>(FRAGMENT);

  if (!UsePageQueryResult.hasData(result)) {
    return null;
  }

  const team = result.data.relationships?.teams.nodes[0];
  const teamImage = getItemImage(team?.heroMedia, DEFAULT_TEAM_IMAGE);

  const company = result.data?.relationships?.companies.nodes[0];

  return (
    <div className={styles.wrapper}>
      {team && team.status === PageStatus.Active && (
        <Item
          heading={t('page.Team', 'Team')}
          name={team?.title ?? ''}
          logo={{ url: teamImage.url, alt: teamImage.alt }}
          url={team?.url ?? ''}
        />
      )}
      {company && (
        <Item
          heading={t('page.Company', 'Company')}
          logo={{ url: company.logo ?? '' }}
          name={company.title}
          url={company.url}
        />
      )}
    </div>
  );
};

interface IItem {
  heading: string;
  name: string;
  url: string;
  logo: { url: string; alt?: string | null } | null;
}

const Item: React.FunctionComponent<IItem> = ({ heading, logo, name, url }) => {
  const DEFAULT_MEDIA = {
    url: TRAINER_IMAGE,
    alt: '',
  };
  const image = logo ?? DEFAULT_MEDIA;

  return (
    <div className={styles.item}>
      <h3 className={`jg-h5 ${styles.heading}`}>{heading}</h3>
      <a
        href={url}
        data-qa="image-link"
        tabIndex={-1}
        itemProp="logo"
        className={styles.logo}
      >
        <img src={image.url} alt={image.alt ?? ''} />
      </a>
      <a className={styles.title} href={url} data-qa="heading-link">
        <h4 className="jg-h3">{name}</h4>
      </a>
    </div>
  );
};

export default TeamAndCompany;

function getItemImage(hero?: Media | null, fallback?: string) {
  return hero && onlyImages(hero) ? hero : { url: fallback, alt: null };
}
